import {
  ArrowLeft,
  BarChart3,
  Check,
  Database,
  MessageCircle,
  Shield,
  TrendingUp,
} from "lucide-react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { cn } from "@/lib/utils";

const CRITERION_ICONS = [BarChart3, Database, TrendingUp, MessageCircle, Shield];

const ADVICE_POINTS = [
  "What is happening.",
  "Why it is happening.",
  "Why it matters.",
  "What evidence supports your view.",
  "What could challenge your view.",
  "What conclusion follows, and how confident you are in it.",
];

interface Band {
  label: string;
  qualifier: string;
  bullets: string[];
}

interface Criterion {
  number: number;
  name: string;
  subtitle: string;
  bands: [Band, Band, Band];
}

const CRITERIA: Criterion[] = [
  {
    number: 1,
    name: "Analytical Reasoning",
    subtitle: "This looks at how well you reason through the problem.",
    bands: [
      {
        label: "LOW-SCORING",
        qualifier: "often...",
        bullets: [
          "Describes events without explaining why they happened.",
          "Lists factors without showing how they connect.",
          "Presents one view without considering alternatives.",
          "Makes broad claims without testing assumptions.",
        ],
      },
      {
        label: "MEDIUM",
        qualifier: "usually...",
        bullets: [
          "Identifies the main drivers of the issue.",
          "Explains basic cause-and-effect relationships.",
          "Acknowledges at least one alternative explanation.",
          "Links evidence to the argument, but may not fully weigh competing views.",
        ],
      },
      {
        label: "HIGH-SCORING",
        qualifier: "tends to...",
        bullets: [
          "Analyses how multiple factors interact.",
          "Compares competing explanations and judges which is stronger.",
          "Tests assumptions and considers alternative scenarios.",
          "Reaches a balanced conclusion that recognises uncertainty.",
        ],
      },
    ],
  },
  {
    number: 2,
    name: "Research Integrity",
    subtitle: "This looks at how responsibly you use and cite evidence.",
    bands: [
      {
        label: "LOW-SCORING",
        qualifier: "often...",
        bullets: [
          "Uses inconsistent or non-credible sources.",
          "Leaves some claims unsupported by evidence.",
          "Attributes sources incompletely or not at all.",
          "Doesn't consider whether data is current enough.",
        ],
      },
      {
        label: "MEDIUM",
        qualifier: "usually...",
        bullets: [
          "Uses credible, primary sources consistently.",
          "Cites all key claims with a source and date.",
          "Describes where the evidence comes from.",
          "Stays within a reasonable recency threshold.",
        ],
      },
      {
        label: "HIGH-SCORING",
        qualifier: "tends to...",
        bullets: [
          "Triangulates across multiple independent datasets.",
          "Evaluates source quality, not just cites it.",
          "Distinguishes primary from secondary evidence.",
          "Handles gaps or conflicting data explicitly.",
        ],
      },
    ],
  },
  {
    number: 3,
    name: "Relevance & Investment Impact",
    subtitle: "This looks at whether you connect the analysis to real-world implications.",
    bands: [
      {
        label: "LOW-SCORING",
        qualifier: "often...",
        bullets: [
          "States no clear implication from the analysis.",
          "Doesn't identify which asset, sector, or policy is affected.",
          "Leaves the reader to infer why the issue matters.",
          "Offers vague or non-directional conclusions.",
        ],
      },
      {
        label: "MEDIUM",
        qualifier: "usually...",
        bullets: [
          "States a specific implication (asset, sector, or policy).",
          "Explains why the issue matters to a decision-maker.",
          "Gives a clear directional view.",
          "Connects the implication back to the analysis.",
        ],
      },
      {
        label: "HIGH-SCORING",
        qualifier: "tends to...",
        bullets: [
          "Quantifies at least one impact scenario with a timeframe.",
          "Distinguishes a base case from a risk scenario.",
          "Structures outcomes conditionally (If X, then Y).",
          "Defends the implication against uncertainty.",
        ],
      },
    ],
  },
  {
    number: 4,
    name: "Clarity & Communication",
    subtitle: "This looks at how clearly and professionally you communicate.",
    bands: [
      {
        label: "LOW-SCORING",
        qualifier: "often...",
        bullets: [
          "Buries or omits the main thesis.",
          "Has inconsistent structure or illogical paragraph flow.",
          "Doesn't signpost how the argument progresses.",
          "Ends without a clear concluding point.",
        ],
      },
      {
        label: "MEDIUM",
        qualifier: "usually...",
        bullets: [
          "States the core thesis clearly up front.",
          "Progresses logically from thesis to evidence to conclusion.",
          "Summarises the key point at the close.",
          "Uses readable, professional language throughout.",
        ],
      },
      {
        label: "HIGH-SCORING",
        qualifier: "tends to...",
        bullets: [
          "Opens with a decision-ready executive summary.",
          "Signposts the argument's progression explicitly.",
          "Distinguishes clearly between fact and forecast.",
          "Closes with one concise, justified takeaway.",
        ],
      },
    ],
  },
  {
    number: 5,
    name: "Professional & Ethical Standards",
    subtitle: "This looks at neutrality, transparency, and professional discipline.",
    bands: [
      {
        label: "LOW-SCORING",
        qualifier: "often...",
        bullets: [
          "Presents opinions without evidential support.",
          "Shows detectable political or directional bias.",
          "Fails to acknowledge limitations or data gaps.",
          "Does not attribute external ideas.",
        ],
      },
      {
        label: "MEDIUM",
        qualifier: "usually...",
        bullets: [
          "Maintains strict analytical neutrality.",
          "Acknowledges limitations and data gaps explicitly.",
          "Attributes all external ideas and evidence.",
          "Maintains a professional tone throughout.",
        ],
      },
      {
        label: "HIGH-SCORING",
        qualifier: "tends to...",
        bullets: [
          "Actively addresses counterarguments and weaknesses.",
          "States confidence levels and their basis.",
          "Proactively discloses conflicts, biases, or methodology limits.",
          "Frames the conclusion within the uncertainty present.",
        ],
      },
    ],
  },
];

const BAND_STYLES = [
  {
    wrapper: "border border-[#ECE8DA] bg-[#FDFBF4]",
    label: "border border-[#EAC8BB] bg-[#FBF2EE] text-[#B0563C]",
    bullet: "bg-[#B0563C]",
  },
  {
    wrapper: "border border-[#ECE8DA] bg-[#FDFBF4]",
    label: "border border-[#E4D3A1] bg-[#FBF6E7] text-[#8A6D1F]",
    bullet: "bg-[#B07E1E]",
  },
  {
    wrapper: "border border-[#CBE3D2] bg-[#F7FBF7]",
    label: "border border-[#CBE3D2] bg-[#F0F8F2] text-[#1E6F4F]",
    bullet: "bg-brand",
  },
];

export default function MarkingGuidePage() {
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto flex flex-col gap-6">
        <Link
          to="/how-to-get-started"
          className="inline-flex items-center gap-2 self-start text-[13px] font-semibold text-[#5C6E63] transition-colors hover:text-[#1E6F4F]"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
          Back to How to get started
        </Link>

        <div>
          <p className="text-[11px] font-bold uppercase tracking-[.16em] text-content-muted">
            Guides
          </p>
          <h1 className="mt-1.5 text-3xl sm:text-4xl font-extrabold tracking-[-.015em] text-content-foreground">
            How to get the best feedback
          </h1>
          <p className="mt-2 max-w-[720px] text-[15px] leading-[1.65] text-[#5C6E63]">
            Your work will be assessed on the quality of your thinking, research, judgement, and
            communication. Strong submissions do more than summarise a topic: they build a clear
            argument, use evidence responsibly, explain why the issue matters, and reach a balanced
            conclusion.
          </p>
        </div>

        {CRITERIA.map((criterion) => {
          const Icon = CRITERION_ICONS[criterion.number - 1];
          return (
            <div
              key={criterion.number}
              className="rounded-[14px] border border-content-border bg-card px-7 py-[26px]"
            >
              <div className="flex items-center gap-3.5">
                <div className="flex h-[42px] w-[42px] flex-none items-center justify-center rounded-[11px] bg-[#E9F1E6]">
                  <Icon className="h-[19px] w-[19px] text-[#1E6F4F]" strokeWidth={1.6} />
                </div>
                <div>
                  <h2 className="text-xl font-bold tracking-[-.01em] text-card-foreground">
                    {criterion.number}. {criterion.name}
                  </h2>
                  <p className="mt-0.5 font-serif text-[13px] italic text-content-muted">
                    {criterion.subtitle}
                  </p>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-3.5 md:grid-cols-3">
                {criterion.bands.map((band, i) => {
                  const style = BAND_STYLES[i];
                  return (
                    <div
                      key={band.label}
                      className={cn("flex flex-col gap-3 rounded-xl px-[18px] py-4", style.wrapper)}
                    >
                      <div className="flex items-baseline gap-2">
                        <span
                          className={cn(
                            "rounded-md px-2 py-1 text-[10px] font-bold tracking-[.12em]",
                            style.label
                          )}
                        >
                          {band.label}
                        </span>
                        <span className="font-serif text-xs italic text-content-muted">
                          {band.qualifier}
                        </span>
                      </div>
                      <ul className="flex flex-col gap-[9px]">
                        {band.bullets.map((bullet) => (
                          <li
                            key={bullet}
                            className="flex gap-[9px] text-[12.5px] leading-[1.55] text-[#28402F]"
                          >
                            <span
                              className={cn(
                                "mt-[7px] h-[5px] w-[5px] flex-none rounded-full",
                                style.bullet
                              )}
                            />
                            {bullet}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Overall advice */}
        <div className="rounded-[14px] bg-[#013220] px-8 py-7 text-[#F2EFE3]">
          <div className="text-[11px] font-bold tracking-[.16em] text-[#F2EFE3]/60">
            OVERALL ADVICE
          </div>
          <h2 className="mt-1.5 text-[22px] font-bold tracking-[-.01em]">
            Strong work is judged, not long
          </h2>
          <p className="mt-2 max-w-[640px] text-sm leading-[1.65] text-[#F2EFE3]/75">
            The strongest submissions are not necessarily the longest. Aim to produce work that is
            structured, evidence-led, relevant, and professionally judged. A strong answer should
            make clear:
          </p>
          <div className="mt-[18px] grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
            {ADVICE_POINTS.map((point) => (
              <div key={point} className="flex items-start gap-2.5 text-[13.5px] leading-[1.55]">
                <Check
                  className="mt-[3px] h-[15px] w-[15px] flex-none text-brand"
                  strokeWidth={2}
                />
                {point}
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
